import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Instagram } from 'lucide-react';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${id}`;
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const navLinks = [
    { name: 'Accueil', id: 'hero' },
    { name: 'Nos Expertises', id: 'expertises' },
    { name: 'Tarifs', id: 'tarifs' },
    { name: 'FAQ', id: 'faq' },
    { name: 'Contact', id: 'contact' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="h-20 w-20 lg:h-24 lg:w-24 rounded-full overflow-hidden shadow-xl transition-transform duration-300 group-hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_ems-lyon-sud/artifacts/86j81t7o_IMG_2591.jpg" 
                alt="The Hidden Garden Logo" 
                className="h-full w-full object-cover scale-110"
                style={{ objectPosition: 'center' }}
              />
            </div>
            <div className="hidden sm:block">
              <div className={`text-xl lg:text-2xl font-bold tracking-wide transition-colors duration-300 ${
                isScrolled ? 'text-emerald-800' : 'text-white'
              }`}>
                THE HIDDEN GARDEN
              </div>
              <div className="text-sm lg:text-base text-yellow-500 font-light tracking-wider">
                CENTRE WELLNESS & EMS
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="text-sm font-medium text-gray-700 hover:text-emerald-700 transition-colors duration-300 relative group"
              >
                {link.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-700 transition-all duration-300 group-hover:w-full"></span>
              </button>
            ))}
            <Link
              to="/cgv"
              className="text-sm font-medium text-gray-700 hover:text-emerald-700 transition-colors duration-300"
            >
              CGV
            </Link>
          </nav>

          {/* Contact Icons & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="tel:0659839111"
              className="text-emerald-700 hover:text-emerald-800 transition-colors duration-300"
              aria-label="Téléphone"
            >
              <Phone className="w-5 h-5" />
            </a>
            <a 
              href="https://www.instagram.com/hiddengarden.ems?igsh=MWp4OHNidTZzbGo3MQ%3D%3D&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-700 hover:text-emerald-800 transition-colors duration-300"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://wa.me/33659839111"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-emerald-700 text-white px-6 py-2.5 rounded-full font-medium hover:bg-emerald-800 transition-all duration-300 hover:shadow-lg hover:scale-105"
            >
              Réserver
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-gray-700 hover:text-emerald-700 transition-colors duration-300"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`lg:hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen 
            ? 'max-h-screen opacity-100' 
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <nav className="bg-white/98 backdrop-blur-md border-t border-gray-200 shadow-xl">
          <div className="container mx-auto px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="block w-full text-left text-base font-medium text-gray-700 hover:text-emerald-700 py-2 transition-colors duration-300"
              >
                {link.name}
              </button>
            ))}
            <Link
              to="/cgv"
              className="block w-full text-left text-base font-medium text-gray-700 hover:text-emerald-700 py-2 transition-colors duration-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              CGV
            </Link>
            <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
              <a 
                href="tel:0659839111"
                className="text-emerald-700 hover:text-emerald-800 transition-colors duration-300"
                aria-label="Téléphone"
              >
                <Phone className="w-6 h-6" />
              </a>
              <a 
                href="https://www.instagram.com/hiddengarden.ems?igsh=MWp4OHNidTZzbGo3MQ%3D%3D&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-700 hover:text-emerald-800 transition-colors duration-300"
                aria-label="Instagram"
              >
                <Instagram className="w-6 h-6" />
              </a>
            </div>
            <a
              href="https://wa.me/33659839111"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center bg-emerald-700 text-white px-6 py-3 rounded-full font-medium hover:bg-emerald-800 transition-all duration-300"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Réserver sur WhatsApp
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};
